import { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const BrochureGateContext = createContext(null);
export const useBrochureGate = () => useContext(BrochureGateContext);

const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal","Other"];
const empty = { name: "", phone: "", email: "", state: "", city: "", current_business: "" };

const RESOURCES = {
  brochure: { title: "Company Brochure", file: `${API}/brochure`, filename: "Saans_Wellness_Brochure.pdf" },
  catalogue: { title: "Product Catalogue", file: `${API}/brochure`, filename: "Saans_Wellness_Catalogue.pdf" },
  pricelist: { title: "Price List", file: `${API}/brochure`, filename: "Saans_Wellness_PriceList.pdf" },
  monopoly: { title: "Monopoly Rights Details", file: `${API}/brochure`, filename: "Saans_Wellness_Monopoly.pdf" },
};

export function BrochureGateProvider({ children }) {
  const [resourceKey, setResourceKey] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const resource = resourceKey ? RESOURCES[resourceKey] : null;

  const openGate = useCallback((key = "brochure") => {
    setResourceKey(key);
    setForm(empty);
    setDone(false);
  }, []);

  const close = () => setResourceKey(null);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerDownload = (res) => {
    // Backend sends Content-Disposition: attachment, so navigating triggers a background download
    // without leaving the page. `download` attribute is ignored cross-origin, and target="_blank"
    // caused browsers to open the 77 MB PDF in a new tab that looked like nothing happened.
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = res.file;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 60_000);
    } catch (e) {
      window.location.href = res.file;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required."); return; }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => String(v).trim() !== ""));
      payload.source = `download:${resourceKey}`;
      payload.message = `Requested ${resource.title}`;
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you! Your download is starting.");
      triggerDownload(resource);
    } catch {
      toast.error("Something went wrong. Please try WhatsApp or the enquiry form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BrochureGateContext.Provider value={{ openGate }}>
      {children}
      <AnimatePresence>
        {resource && (
          <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="brochure-gate">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white card-shadow-lg">
              <div className="flex items-start justify-between gap-4 bg-[#1f5d3a] p-6 text-white">
                <div>
                  <div className="inline-flex items-center gap-2 text-[#bfe0cd]"><FileDown size={16} /><span className="eyebrow text-[#bfe0cd]">Free Download</span></div>
                  <h3 className="mt-2 font-serif text-2xl">{resource.title}</h3>
                  <p className="mt-1 text-sm text-[#d6e6dc]">Enter your details to unlock the download instantly.</p>
                </div>
                <button onClick={close} data-testid="brochure-gate-close" className="text-white/80 hover:text-white"><X size={20} /></button>
              </div>

              {done ? (
                <div className="p-8 text-center" data-testid="brochure-gate-success">
                  <CheckCircle2 size={40} className="mx-auto text-[#1f5d3a]" />
                  <h4 className="mt-4 font-serif text-2xl text-[#202124]">Download started!</h4>
                  <p className="mt-2 text-sm text-[#6b6a65]">If it didn't open, click below.</p>
                  <button onClick={() => triggerDownload(resource)} data-testid="brochure-gate-redownload"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#d46a1d] px-6 py-3 text-sm font-semibold text-white">
                    <FileDown size={16} /> Download {resource.title}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4 p-6" data-testid="brochure-gate-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input className="form-field" placeholder="Full name *" value={form.name} onChange={update("name")} data-testid="gate-name" />
                    <input className="form-field" placeholder="Phone *" value={form.phone} onChange={update("phone")} data-testid="gate-phone" />
                    <input type="email" className="form-field" placeholder="Email" value={form.email} onChange={update("email")} data-testid="gate-email" />
                    <select className="form-field" value={form.state} onChange={update("state")} data-testid="gate-state">
                      <option value="">State</option>
                      {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input className="form-field sm:col-span-2" placeholder="City / District" value={form.city} onChange={update("city")} data-testid="gate-city" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid="gate-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-6 py-3.5 text-base font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
                    {submitting ? "Please wait…" : <>Get {resource.title} <FileDown size={17} /></>}
                  </button>
                  <p className="text-center text-xs text-[#8a8880]">Your details help our team assist you. No spam, ever.</p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BrochureGateContext.Provider>
  );
}
